import {
  type DealInsightsChannelKeysApi,
  type DealInsightsDirectnessKeysApi,
  type DealInsightsInventoryDistributionsApi,
  type DealInsightsMediaTypeKeysApi,
  dealInsightsChannelKeysApi,
  dealInsightsDirectnessKeysApi,
  dealInsightsMediaTypeKeysApi,
} from '@openx/types/dealInsights';
import type { TFunction } from 'i18next';
import type { DistributionSectionData, DistributionsChartData } from '../types';
import { getColorForDspName } from './getColorForDspName';
import { getColorFromDistributionKeys } from './getColorFromDistributionKeys';
import { getLabelFromDistributionKeys } from './getLabelFromDistributionKeys';

const mapDistributionSection = (
  t: TFunction,
  data: DealInsightsInventoryDistributionsApi | undefined,
  sectionKey: keyof DistributionsChartData,
  directnessKeys: DealInsightsDirectnessKeysApi[],
  mediaTypeKeys: DealInsightsMediaTypeKeysApi[],
  channelKeys: DealInsightsChannelKeysApi[]
): DistributionSectionData => {
  const dspData = data?.dsp_name?.[sectionKey];
  const validEntries: [string, number][] = dspData
    ? Object.entries(dspData)
        .filter(([, value]) => Boolean(value))
        .map(([name, value]) => [name || '__unknown__', Number(value)])
    : [];

  const top5 = validEntries.slice(0, 5);
  const othersValue = validEntries.slice(5).reduce((sum, [, value]) => sum + value, 0);

  return {
    directness: directnessKeys.map(key => ({
      name: getLabelFromDistributionKeys(key, t),
      value: data?.directness[sectionKey][key] ?? 0,
      color: getColorFromDistributionKeys(key),
    })),
    media_type: mediaTypeKeys.map(key => ({
      name: getLabelFromDistributionKeys(key, t),
      value: data?.media_type[sectionKey][key] ?? 0,
      color: getColorFromDistributionKeys(key),
    })),
    channel: channelKeys.map(key => ({
      name: getLabelFromDistributionKeys(key, t),
      value: data?.channel[sectionKey][key] ?? 0,
      color: getColorFromDistributionKeys(key),
    })),
    dsp_name: [
      ...top5.map(([name, value], index) => ({
        name: name === '__unknown__' ? t('Unknown') : name,
        value,
        color: getColorForDspName(index),
      })),
      ...(othersValue > 0 ? [{ name: t('Others'), value: othersValue, color: getColorForDspName(5) }] : []),
    ],
  };
};

export const mapDistributionsApiData = (
  t: TFunction,
  data?: DealInsightsInventoryDistributionsApi
): DistributionsChartData => {
  const directnessKeys = [dealInsightsDirectnessKeysApi.TRUE, dealInsightsDirectnessKeysApi.FALSE];
  const mediaTypeKeys = [
    dealInsightsMediaTypeKeysApi.CTV,
    dealInsightsMediaTypeKeysApi.DISPLAY,
    dealInsightsMediaTypeKeysApi.OLV,
  ];
  const channelKeys = [dealInsightsChannelKeysApi.SITE, dealInsightsChannelKeysApi.APP];

  const distributionSections: (keyof DistributionsChartData)[] = ['spend', 'impressions', 'clicks'];

  return distributionSections.reduce(
    (acc, sectionKey) => {
      acc[sectionKey] = mapDistributionSection(t, data, sectionKey, directnessKeys, mediaTypeKeys, channelKeys);
      return acc;
    },
    {} as Record<keyof DistributionsChartData, DistributionSectionData>
  );
};
